/* ============================================================================
   3D BUILDER  -  SHAREABLE AND RESUMABLE BUILDS

   A configuration used to exist only as long as the tab. It could not be sent
   to anyone, posted, or returned to - so every visitor who was nearly ready to
   buy and got interrupted started again from the default bike, if they came
   back at all.

   Two features, one encoding:

     SHARE    the whole configuration goes in the URL. Copy the link and
              whoever opens it lands on that exact bike.

     RESUME   the same string is kept in localStorage, so coming back later
              reopens the build in progress.

   PRECEDENCE
   A link beats a saved build, always. Someone who follows a shared link is
   asking to see that bike; quietly overriding it with whatever they were
   fiddling with last week would be baffling.

   HOW IT APPLIES A CONFIG
   Not by reaching into the section's state - `selected`, `props` and the rest
   are locals inside its IIFE. It finds the matching swatch and clicks it, and
   the section's own handler does everything it normally does: sets the option,
   recolours the mesh, runs the part swaps, recalculates the price. Nothing
   here duplicates that logic, so nothing here can drift out of sync with it.

   The trade-off is that it has to wait for the model, because a click before
   the GLB lands would move the price but leave the bike on default parts.
   ============================================================================ */
(function () {

  var PARAM = 'build';
  var STORE_KEY = 'pm-build-v1:' + location.pathname;
  var MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30;   /* a month */

  /* ---- Encoding ----------------------------------------------------------
     Base64 of compact JSON. Not readable in the URL bar, which is a fair cost:
     the alternative is a query string full of percent-encoded spaces from
     values like "Guts Cover" and "No ODI", which chat apps mangle when people
     paste them. URL-safe characters only, and no padding. */
  function encode(cfg) {
    try {
      return btoa(unescape(encodeURIComponent(JSON.stringify(cfg))))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return null; }
  }

  function decode(str) {
    try {
      var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      return JSON.parse(decodeURIComponent(escape(atob(b64))));
    } catch (e) { return null; }
  }

  function root() { return document.getElementById('pm-builder'); }

  function readConfig() {
    var el = root();
    if (!el) return null;

    var cfg = { v: [], p: {} };

    var variants = el.querySelectorAll('.pm-sw--variant.is-on');
    for (var i = 0; i < variants.length; i++) {
      var idx = parseInt(variants[i].getAttribute('data-optindex'), 10);
      if (!isNaN(idx)) cfg.v[idx] = variants[i].getAttribute('data-value');
    }

    var props = el.querySelectorAll('.pm-sw--prop.is-on');
    for (var j = 0; j < props.length; j++) {
      cfg.p[props[j].getAttribute('data-prop')] = props[j].getAttribute('data-value');
    }

    return cfg;
  }

  function quote(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function pick(selector) {
    var el = root();
    var btn = el && el.querySelector(selector);
    if (btn && !btn.classList.contains('is-on')) btn.click();
  }

  function applyConfig(cfg) {
    if (!cfg) return;

    var list = cfg.v || [];
    for (var i = 0; i < list.length; i++) {
      if (!list[i]) continue;
      pick('.pm-sw--variant[data-optindex="' + i + '"][data-value="' + quote(list[i]) + '"]');
    }

    var props = cfg.p || {};
    for (var key in props) {
      if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
      pick('.pm-sw--prop[data-prop="' + quote(key) + '"][data-value="' + quote(props[key]) + '"]');
    }
  }

  function shareUrl() {
    var cfg = readConfig();
    if (!cfg) return null;
    var code = encode(cfg);
    return code ? location.origin + location.pathname + '?' + PARAM + '=' + code : null;
  }

  /* ---- Storage -----------------------------------------------------------
     Every read and write is guarded: localStorage throws in private mode on
     some browsers, and losing a saved build is never worth breaking the page
     over. */
  function save() {
    try {
      var cfg = readConfig();
      if (!cfg) return;
      localStorage.setItem(STORE_KEY, JSON.stringify({ at: Date.now(), cfg: cfg }));
    } catch (e) {}
  }

  function load() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.cfg) return null;
      if (Date.now() - (saved.at || 0) > MAX_AGE_MS) { clear(); return null; }
      return saved.cfg;
    } catch (e) { return null; }
  }

  function clear() {
    try { localStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  /* ---- UI ---------------------------------------------------------------- */
  function css() {
    if (document.getElementById('pm-share-css')) return;
    var s = document.createElement('style');
    s.id = 'pm-share-css';
    s.textContent =
      '.pm-share{width:100%;margin-top:.6rem;padding:.8rem 1rem;border:1px solid rgba(255,255,255,.22);' +
      'border-radius:12px;background:transparent;color:rgba(255,255,255,.78);font-family:inherit;' +
      'font-size:.72rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;' +
      'transition:background .14s ease,border-color .14s ease,color .14s ease;}' +
      '.pm-share:hover{background:rgba(255,255,255,.07);border-color:rgba(255,255,255,.45);color:#fff;}' +
      '.pm-share.is-done{background:#fff;border-color:#fff;color:#0a0a0a;}' +
      '.pm-resumed{display:flex;align-items:center;gap:.6rem;margin:0 0 1rem;padding:.6rem .75rem;' +
      'border:1px solid rgba(255,255,255,.14);border-radius:12px;background:rgba(255,255,255,.04);' +
      'color:rgba(255,255,255,.6);font-size:.68rem;font-weight:600;}' +
      '.pm-resumed button{margin-left:auto;padding:.3rem .6rem;border:1px solid rgba(255,255,255,.28);' +
      'border-radius:8px;background:transparent;color:#fff;font-family:inherit;font-size:.62rem;' +
      'font-weight:700;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;}' +
      '.pm-resumed button:hover{background:rgba(255,255,255,.1);}';
    (document.head || document.documentElement).appendChild(s);
  }

  function copy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:absolute;left:-9999px;top:0;';
      document.body.appendChild(field);
      field.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(field);
      if (ok) { resolve(); } else { reject(); }
    });
  }

  function hasButton() {
    var el = root();
    return !!(el && el.querySelector('.pm-share'));
  }

  function mountButton() {
    var el = root();
    if (!el) return false;
    if (hasButton()) return true;

    var addcart = el.querySelector('.pm-addcart');
    if (!addcart) return false;

    css();

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pm-share';
    btn.textContent = 'Copy build link';

    btn.addEventListener('click', function () {
      var url = shareUrl();
      if (!url) return;
      copy(url).then(function () {
        btn.textContent = 'Link copied';
        btn.classList.add('is-done');
        window.setTimeout(function () {
          btn.textContent = 'Copy build link';
          btn.classList.remove('is-done');
        }, 2000);
      }).catch(function () {
        window.prompt('Copy this link:', url);
      });
    });

    /* After Add to cart: sharing is the second thing you want, never the
       first. */
    addcart.parentNode.insertBefore(btn, addcart.nextSibling);
    return true;
  }

  /* Restoring silently would be unnerving - the bike is not the one the page
     advertises and there is no way back. So it says so, and offers the way
     back. */
  function showResumed() {
    var el = root();
    var panel = el && el.querySelector('.pm-builder-opts');
    if (!panel || panel.querySelector('.pm-resumed')) return;

    css();

    var note = document.createElement('div');
    note.className = 'pm-resumed';
    note.innerHTML = '<span>Picked up where you left off</span>';

    var reset = document.createElement('button');
    reset.type = 'button';
    reset.textContent = 'Start over';
    reset.addEventListener('click', function () {
      clear();
      location.href = location.origin + location.pathname;
    });

    note.appendChild(reset);
    panel.insertBefore(note, panel.firstChild);
  }

  /* ---- Startup ----------------------------------------------------------- */
  var fromLink = (function () {
    var match = location.search.match(new RegExp('[?&]' + PARAM + '=([^&]+)'));
    return match ? decode(match[1]) : null;
  })();

  var fromStore = fromLink ? null : load();   /* a link always wins */
  var incoming = fromLink || fromStore;
  var applied = false;

  function modelReady() {
    var el = root();
    if (!el || !el.querySelector('.pm-sw--prop')) return false;
    return !!(window.__pmScene && window.__pmScene.model);
  }

  var ticks = 0;
  var poll = window.setInterval(function () {
    ticks++;

    var buttonDone = mountButton();

    if (incoming && !applied && modelReady()) {
      applyConfig(incoming);
      applied = true;
      if (fromStore) showResumed();
    }

    if ((buttonDone && (!incoming || applied)) || ticks > 160) window.clearInterval(poll);
  }, 300);

  /* Save on change. Debounced, and never before the incoming config has been
     applied - otherwise the first tick would overwrite a saved build with the
     page defaults. */
  var saveTimer = null;
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('.pm-sw')) return;
    if (incoming && !applied) return;
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(save, 400);
  });

  window.__pmShare = {
    url: shareUrl,
    read: readConfig,
    apply: applyConfig,
    save: save,
    load: load,
    clear: clear
  };
})();
